import React, { useCallback, useEffect, useState } from "react";
import CustomCalendar from "../elements/custom-calendar";
import { Button, notification } from "antd";
import DataProvider from "../../services";
import moment from "moment";
import { Dayjs } from "dayjs";
import LoadWrapper from "../elements/load-wrapper";
import { schedulePassed } from "../../utils";

type NotificationType = "success" | "info" | "warning" | "error";
export default function SelectSchedule(props: any) {
  const [api, contextHolder] = notification.useNotification();
  const { persist, setPersist } = props;
  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<any[]>([]);
  const [schedule, setSchedule] = useState<Dayjs | null>(null);
  const [time, setTime] = useState<any>(null);
  const [timeFrames, setTimeFrames] = useState<any[]>([]);
  const [slots, setSlots] = useState<any[] | null>(null);

  const openNotificationWithIcon = (
    type: NotificationType,
    title: string,
    message: string,
  ) => {
    api[type]({
      message: title,
      description: message,
    });
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const results = await DataProvider.getAvailableSchedule();
      const schedules = results.data ?? [];
      const slts = schedules.filter(
        (value: any) =>
          moment(value.date, "DD-MM-YYYY").format("DD MM YYYY") ===
          moment(new Date()).format("DD MM YYYY"),
      );
      let arr: any[] = [];
      for (const value of slts) {
        // Tag each timeframe with the schedule it actually belongs to, so
        // submitting always attaches the correct schedule - not just the
        // first schedule entry for the day (see _onProceed below).
        value.timeFrames.forEach((val: any) =>
          arr.push({ ...val, _scheduleId: value.id })
        );
      }

      // Sort time frames in chronological order
      const sortedTimeFrames = arr.sort((a, b) =>
        moment(a.startTime, "HH:mm").diff(moment(b.startTime, "HH:mm")),
      );

      setTimeFrames(sortedTimeFrames);
      setSlots(slts);
      setData(results.data);
    } catch {
      // Keep existing data on error to prevent UI from breaking
    } finally {
      setLoading(false);
    }
  }, []);

  // Update timeframes when schedule changes
  const updateTimeFrames = useCallback(
    (selectedSchedule: Dayjs | null, scheduleData: any[]) => {
      if (!selectedSchedule || scheduleData.length === 0) {
        setTimeFrames([]);
        setSlots(null);
        return;
      }

      setLoading(true);
      setTime(null);

      const sltss = scheduleData.filter(
        (value) =>
          moment(selectedSchedule.toDate()).format("DD MM YYYY") ===
          moment(value.date, "DD-MM-YYYY").format("DD MM YYYY"),
      );

      let arr: any[] = [];
      for (const value of sltss) {
        arr.push(
          ...value.timeFrames.map((val: any) => ({
            ...val,
            _scheduleId: value.id,
          }))
        );
      }

      const sortedTimeFrames = arr.sort((a, b) =>
        moment(a.startTime, "HH:mm").diff(moment(b.startTime, "HH:mm")),
      );

      setTimeFrames(sortedTimeFrames);
      setSlots(sltss);
      setLoading(false);
    },
    [],
  );

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Only update timeframes if we have both schedule and data
    if (schedule && data.length > 0) {
      updateTimeFrames(schedule, data);
    }
  }, [schedule, data, updateTimeFrames]);

  function _onProceed() {
    if (!slots || !time) {
      return openNotificationWithIcon(
        "warning",
        "Schedule",
        "Kindly select one of the dates and time!",
      );
    }
    // Use the schedule the clicked timeframe actually belongs to - not
    // always the first schedule entry for the day - so a day split across
    // multiple schedule blocks books the correct one.
    const matchedSchedule =
      slots.find((s: any) => s.id === time._scheduleId) || slots[0];
    setPersist({ ...persist, time, schedule: matchedSchedule });
    props.setTab(2);
  }

  return (
    <LoadWrapper loading={loading}>
      <div className="flex justify-center">
        <div className="md:w-[50%] w-[90%]">
          <h3 className="font-semibold text-sm text-gray-600 my-2">
            Select a schedule
          </h3>
          <div className="">
            <CustomCalendar setSchedule={setSchedule} />
          </div>
          <div>
            <h3 className="font-semibold text-sm text-gray-600 mt-4 mb-2">
              Select a time
            </h3>
            <div className="flex justify-start w-full flex-wrap">
              {timeFrames.length > 0 ? (
                timeFrames
                  .filter((value: any) => !value.booked)
                  .map((i: any, idx: any) => {
                    const passed: boolean = schedulePassed(
                      moment(schedule?.toDate()).format("DD-MM-YYYY"),
                      i.startTime,
                    );
                    return !passed ? (
                      <span
                        key={idx}
                        onClick={() => setTime(i)}
                        className={`${
                          i === time
                            ? "border-2 border-solid border-blue-800 bg-[#F0F9FF] text-[#06509E]"
                            : "border border-solid border-gray-300"
                        }  p-2 m-2 rounded-md cursor-pointer`}
                      >
                        {i.startTime} - {i.endTime}
                      </span>
                    ) : null;
                  })
              ) : (
                <p className="my-5">
                  No available slots on{" "}
                  {schedule &&
                    moment(schedule?.toDate()).format("Do MMMM YYYY")}
                </p>
              )}
            </div>
          </div>
          <div className="my-5">
            <Button
              onClick={_onProceed}
              type="primary"
              size="large"
              color="#0081D4"
              htmlType="submit"
              className="bg-[#0081D4] w-full"
            >
              Continue
            </Button>
            <Button
              size="large"
              onClick={() => props.setTab(0)}
              className="w-full my-3"
            >
              Back
            </Button>
          </div>
        </div>
      </div>
      {contextHolder}
    </LoadWrapper>
  );
}
